import { T, useTolgee } from '@tolgee/react';
import { Modal, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, CrossSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import ClickSvg from '../../../assets/icons/ClickSvg';
import ProfileChecked from '../../../assets/icons/ProfileChecked';
import theme from '../../../assets/theme';
import { AiAssessedScore } from '../../../frontendServices/gameAPI';
import { useMetrics } from '../../../hooks/useMetrics';
import { Response } from '../../../services/backendService/types';
import {
  hasStrengthBeenRevealed,
  setStrengthRevealed,
} from '../../../utils/liveGameStateStorage';
import { Button } from '../../_atoms/Button';
import { LoadingDots } from '../../_atoms/DismissableQuestionList/DismissableQuestionList';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { MarkdownSlateViewer } from '../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';
import LiveGameImage from '../LiveGameImage';

// Helper function to highlight matching text
const HighlightedText = ({
  text,
  highlightText,
}: {
  text: string;
  highlightText: string;
}) => {
  if (!highlightText || !text) {
    return <>{text}</>;
  }

  // Case-insensitive search for the highlight text
  const regex = new RegExp(
    `(${highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <HighlightSpan key={index}>{part}</HighlightSpan>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
};

const HighlightSpan = styled.span`
  background-color: ${theme.colors.translucentOrange};
  color: ${theme.colors.black};
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: bold;
`;

const StrengthContainer = ({
  gameId,
  responseId,
  strength,
  isRevealed,
  setIsRevealed,
}: {
  gameId: string;
  responseId: string;
  strength: { description: string; strengthText: string } | null;
  isRevealed: boolean;
  setIsRevealed: (isRevealed: boolean) => void;
}) => {
  const metrics = useMetrics();
  return (
    <>
      {!isRevealed && (
        <RevealOverlay
          onClick={() => {
            metrics.logEvent('AIResponseReviewView.StrengthRevealed');
            setStrengthRevealed(gameId, responseId);
            setIsRevealed(true);
          }}
        >
          <ClickIconContainer>
            <IconWrapper color="#ffffff04" height="40%" width="40%">
              <ClickSvg />
            </IconWrapper>
            <span>
              <T keyName="Reveal" defaultValue="Reveal" />
            </span>
          </ClickIconContainer>
        </RevealOverlay>
      )}
      <Feedback>
        <MarkdownSlateViewer content={strength?.description || ''} fontSize={28} />
      </Feedback>
    </>
  );
};

const RevealOverlay = styled.div`
  background: ${theme.colors.red}07;
  backdrop-filter: blur(10px);
  position: absolute;
  top: 30px;
  left: 0px;
  width: 100%;
  height: 100%;
  min-height: 200px;
  z-index: 1;
  cursor: pointer;

  span {
    font-size: 40px;
    text-align: center;
    color: ${theme.colors.white};
  }
`;

const ClickIconContainer = styled.div`
  position: absolute;
  top: calc(50% - 40px);
  left: 50%;
  height: 50%;
  width: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  justify-content: left;
  align-items: center;
  gap: 0px;
  height: fit-content;
`;

const FeedbackReview = ({
  aiAssessedScore,
  response,
  isStrengthRevealed,
  setIsStrengthRevealed,
  gameId,
  onStrengthViewChange,
}: {
  aiAssessedScore?: AiAssessedScore;
  response?: Response | undefined;
  isStrengthRevealed: boolean;
  setIsStrengthRevealed: (isStrengthRevealed: boolean) => void;
  gameId: string;
  onStrengthViewChange?: (isStrengthInView: boolean) => void;
}) => {
  const { t } = useTolgee();
  const metrics = useMetrics();
  const [isFeedbackSelected, setIsFeedbackSelected] = useState<boolean>();

  useEffect(() => {
    // If there's a strength, prioritize showing it; otherwise show feedback
    if (response?.strength?.strengthText) {
      setIsFeedbackSelected(false);
    } else if (aiAssessedScore?.feedback) {
      setIsFeedbackSelected(true);
    }
  }, [aiAssessedScore?.feedback, response?.strength?.strengthText]);

  useEffect(() => {
    // Notify parent when strength view changes
    onStrengthViewChange?.(!isFeedbackSelected);
  }, [isFeedbackSelected, onStrengthViewChange]);

  return (
    <BottomBlurContainer>
      <FeedbackStrengthContainer>
        <SwitchContainer>
          <FeedbackSwitch
            styles={{
              borderRadius: response?.strength?.strengthText
                ? '10px 0px 0px 0px'
                : '10px 0px 10px 0px',
              cursor: response?.strength?.strengthText ? 'pointer' : 'default',
            }}
            onClick={() => {
              metrics.logEvent('AIResponseReviewView.FeedbackSelected');
              setIsFeedbackSelected(true);
            }}
            $isFeedbackSelected={!!isFeedbackSelected}
          >
            {t('Feedback')}
          </FeedbackSwitch>
          {response?.strength?.strengthText && (
            <StrengthSwitch
              onClick={() => {
                metrics.logEvent('AIResponseReviewView.StrengthSelected');
                setIsFeedbackSelected(false);
              }}
              $isFeedbackSelected={!isFeedbackSelected}
            >
              {t('Strength')}
            </StrengthSwitch>
          )}
        </SwitchContainer>
        <FeedbackScrollInner>
          {isFeedbackSelected && aiAssessedScore?.feedback && (
            <Feedback>
              <MarkdownSlateViewer content={aiAssessedScore?.feedback} fontSize={28} />
            </Feedback>
          )}
          {!isFeedbackSelected && response?.id && (
            <StrengthContainer
              strength={response?.strength || null}
              gameId={gameId}
              responseId={response.id}
              isRevealed={isStrengthRevealed}
              setIsRevealed={setIsStrengthRevealed}
            ></StrengthContainer>
          )}
        </FeedbackScrollInner>
      </FeedbackStrengthContainer>
    </BottomBlurContainer>
  );
};

const FeedbackStrengthContainer = styled.div`
  background: ${theme.colors.black};
  color: ${theme.colors.white};
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  padding: 40px 0px 0px 0px;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`;

const SwitchContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: fit-content;
  border-radius: opx 0px 5px 0px;
  overflow: hidden;
  display: flex;
`;

const FeedbackSwitch = styled(Button)<{ $isFeedbackSelected: boolean }>`
  background: #9747ff50;
  color: ${theme.colors.white};
  border-radius: 0px 0px 0px 0px;
  padding: 5px 10px;

  ${({ $isFeedbackSelected }) =>
    $isFeedbackSelected &&
    css`
      opacity: 1;
      background: #9747ff;
    `};

  &:hover,
  &:focus,
  &:active {
    opacity: 1;
    color: ${theme.colors.white};
    background: #9747ff;
  }
`;

const BottomBlurContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  background: ${theme.colors.black};
  border-radius: 10px;
  overflow: hidden;

  :after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 40px;
    background: linear-gradient(
      to bottom,
      ${theme.colors.black}00,
      ${theme.colors.black}
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const FeedbackScrollInner = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-left: 10px;
  padding-right: 10px;
  padding-bottom: 20px;
`;

const Feedback = styled.div`
  width: 100%;
  color: ${theme.colors.white};
  span {
    color: ${theme.colors.white};
    font-size: 28px;
  }
`;

const StrengthSwitch = styled(FeedbackSwitch)`
  border-radius: 0px 0px 10px 0px;
`;

const AIResponseReviewView = ({
  visible,
  onDone,
  response,
  gameId,
  aiAssessedScore,
  isLoadingScores,
  isDrawing,
  tenantId,
  isAuthenticated,
  onPrevious,
  onNext,
  responses,
  slideId,
}: {
  response?: Response | undefined;
  gameId: string;
  visible: boolean;
  isDrawing?: boolean;
  onDone: () => void;
  aiAssessedScore?: AiAssessedScore;
  isLoadingScores?: boolean;
  tenantId?: string;
  isAuthenticated?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  responses?: Response[];
  slideId?: string;
}) => {
  const metrics = useMetrics();
  const [isStrengthInView, setIsStrengthInView] = useState<boolean>(false);
  const [isStrengthRevealed, setIsStrengthRevealed] = useState<boolean>(false);
  const { t } = useTolgee();
  // Check consolidated storage every time responseId changes to get the reveal state for this specific response
  useEffect(() => {
    if (!response?.id || !gameId) {
      setIsStrengthRevealed(false);
      return;
    }

    const hasRevealedStrength = hasStrengthBeenRevealed(gameId, response.id);
    setIsStrengthRevealed(hasRevealedStrength);
  }, [gameId, response?.id]);

  // Calculate if we're at the first or last response
  const currentIndex = responses?.findIndex(r => r.id === response?.id) ?? -1;
  const isFirstResponse = currentIndex === 0;
  const isLastResponse = currentIndex === (responses?.length ?? 0) - 1;

  // Find the last response with a strength (last of the top picks)
  const responsesWithStrength = responses?.filter(r => r.strength?.strengthText) ?? [];
  const lastTopPickIndex =
    responses?.findIndex(
      r => r.id === responsesWithStrength[responsesWithStrength.length - 1]?.id,
    ) ?? -1;

  // Track when modal opens - dispatch event (LiveGame persists + updates local state)
  useEffect(() => {
    if (visible && response?.id && gameId && slideId) {
      window.dispatchEvent(
        new CustomEvent('aiModalOpened', {
          detail: { gameId, slideId },
        }),
      );
    }
  }, [visible, response?.id, gameId, slideId]);

  return (
    <ModalWrapper
      visible={visible}
      onCancel={onDone}
      onOk={onDone}
      footer={null}
      bodyStyle={{ padding: 0 }}
      closable={false}
      centered
      width="100vw"
    >
      <BodyContainer>
        <h1
          style={{
            fontSize: '36px',
            color: theme.colors.white,
            marginBottom: '20px',
            fontWeight: '800',
          }}
        >
          {isStrengthInView ? (
            <T
              keyName="What makes this a good example?"
              defaultValue="What makes this a good example?"
            />
          ) : aiAssessedScore?.feedback || isLoadingScores ? (
            <T
              keyName="Take a look at the feedback"
              defaultValue="Take a look at the feedback"
            />
          ) : (
            ''
          )}
        </h1>
        <div style={{ width: '100%', display: 'flex', height: '80%' }}>
          {aiAssessedScore?.feedback ? (
            <StyledResponseAndFeedback>
              {isDrawing && response && tenantId && (
                <ResponseBlurContainer>
                  <ResponseContainer>
                    <ResponseHeader>
                      <T keyName="Student Response" defaultValue="Student Response" />
                    </ResponseHeader>
                    <ResponseScrollInner>
                      <LiveGameImage
                        tenantId={tenantId}
                        modifiedAt={1}
                        imageId={response.response}
                        style={{ width: '100%', background: 'white' }}
                        objectFit="contain"
                        boxShadow="none"
                        fluid
                        isAuthenticated={!!isAuthenticated}
                      />
                    </ResponseScrollInner>
                  </ResponseContainer>
                </ResponseBlurContainer>
              )}
              {!isDrawing && (
                <ResponseBlurContainer>
                  <ResponseContainer>
                    {response?.strength && response.strength.strengthText && (
                      <GreatPickTag>
                        <ProfileChecked />
                      </GreatPickTag>
                    )}
                    <ResponseHeader>
                      <T keyName="Student Response" defaultValue="Student Response" />
                    </ResponseHeader>
                    <ResponseScrollInner>
                      {response?.strength &&
                      isStrengthInView &&
                      response.strength.strengthText ? (
                        <HighlightedText
                          text={response.response || ''}
                          highlightText={response.strength.strengthText}
                        />
                      ) : (
                        response?.response
                      )}
                    </ResponseScrollInner>
                  </ResponseContainer>
                </ResponseBlurContainer>
              )}
              <FeedbackReview
                aiAssessedScore={aiAssessedScore}
                response={response}
                isStrengthRevealed={isStrengthRevealed}
                gameId={gameId}
                setIsStrengthRevealed={setIsStrengthRevealed}
                onStrengthViewChange={setIsStrengthInView}
              />
            </StyledResponseAndFeedback>
          ) : isLoadingScores ? (
            <StyledResponseAndFeedback>
              {isDrawing && response && tenantId && (
                <ResponseBlurContainer>
                  <ResponseContainer>
                    <ResponseHeader>
                      <T keyName="Student Response" defaultValue="Student Response" />
                    </ResponseHeader>
                    <ResponseScrollInner>
                      <LiveGameImage
                        tenantId={tenantId}
                        modifiedAt={1}
                        imageId={response.response}
                        style={{ width: '100%', background: 'white' }}
                        objectFit="contain"
                        boxShadow="none"
                        fluid
                        isAuthenticated={!!isAuthenticated}
                      />
                    </ResponseScrollInner>
                  </ResponseContainer>
                </ResponseBlurContainer>
              )}
              {!isDrawing && (
                <ResponseBlurContainer>
                  <ResponseContainer>
                    <ResponseHeader>
                      <T keyName="Student Response" defaultValue="Student Response" />
                    </ResponseHeader>
                    <ResponseScrollInner>
                      {response?.strength &&
                      isStrengthInView &&
                      response.strength.strengthText ? (
                        <HighlightedText
                          text={response.response || ''}
                          highlightText={response.strength.strengthText}
                        />
                      ) : (
                        response?.response
                      )}
                    </ResponseScrollInner>
                  </ResponseContainer>
                </ResponseBlurContainer>
              )}
              <FeedbackContainer>
                {t('loadingFeedback', 'Loading feedback')} <LoadingDots>...</LoadingDots>
              </FeedbackContainer>
            </StyledResponseAndFeedback>
          ) : isDrawing && response && tenantId ? (
            <CenteredImageContainer>
              <LiveGameImage
                tenantId={tenantId}
                modifiedAt={response.modifiedAt || 1}
                imageId={response.response}
                style={{ width: '100%', maxHeight: '85vh', background: 'white' }}
                objectFit="contain"
                boxShadow="0px 2px 20px rgba(0, 0, 0, 0.1)"
                fluid
                isAuthenticated={!!isAuthenticated}
              />
            </CenteredImageContainer>
          ) : (
            <CenteredResponseWrapper>
              <ResponseBlurContainer>
                <ResponseContainer>
                  <ResponseHeader>
                    <T keyName="Student Response" defaultValue="Student Response" />
                  </ResponseHeader>
                  <ResponseScrollInner>{response?.response}</ResponseScrollInner>
                </ResponseContainer>
              </ResponseBlurContainer>
            </CenteredResponseWrapper>
          )}
        </div>
        {/* Navigation buttons in bottom right corner */}
        {responses && responses.length > 1 && (
          <NavigationButtonsContainer>
            <Tooltip
              mouseEnterDelay={1}
              title={
                isFirstResponse
                  ? t('This is the first response', 'This is the first response')
                  : ''
              }
            >
              <NavigationButton
                icon={
                  <IconWrapper height="30px" width="30px" style={{ cursor: 'inherit' }}>
                    <ArrowLeftSvg />
                  </IconWrapper>
                }
                onClick={() => {
                  if (isFirstResponse) return;
                  metrics.logEvent('AIResponseReviewView.PreviousButtonClicked');
                  onPrevious?.();
                }}
                $isDisabled={isFirstResponse}
              ></NavigationButton>
            </Tooltip>

            <DoneReviewingButton
              onClick={() => {
                metrics.logEvent('AIResponseReviewView.CloseButtonClicked');
                onDone();
              }}
              $isActive={currentIndex >= lastTopPickIndex && lastTopPickIndex !== -1}
            >
              <span>
                <T keyName="close" defaultValue="Close" />
              </span>
            </DoneReviewingButton>

            <Tooltip
              mouseEnterDelay={1}
              title={
                isLastResponse
                  ? t('thisIsTheLastResponse', 'This is the last response')
                  : ''
              }
            >
              <NavigationButton
                $isDisabled={isLastResponse}
                $isOrange={
                  !(currentIndex >= lastTopPickIndex && lastTopPickIndex !== -1) &&
                  !isLastResponse
                }
                icon={
                  <IconWrapper height="30px" width="30px" style={{ cursor: 'inherit' }}>
                    <ArrowRightSvg />
                  </IconWrapper>
                }
                onClick={() => {
                  if (isLastResponse) return;
                  metrics.logEvent('AIResponseReviewView.NextButtonClicked');
                  onNext?.();
                }}
              ></NavigationButton>
            </Tooltip>
          </NavigationButtonsContainer>
        )}
        {responses && responses.length === 1 && (
          <NavigationButtonsContainer>
            {responsesWithStrength.length > 0 ? (
              <Tooltip
                mouseEnterDelay={1}
                title={t(
                  'youveReviewedTheTopResponse',
                  "You've reviewed the top response",
                )}
              >
                <DoneReviewingButton onClick={onDone} $isActive={true}>
                  <span>
                    <T keyName="close" defaultValue="Close" />
                  </span>
                </DoneReviewingButton>
              </Tooltip>
            ) : (
              <NavigationButton
                $isDisabled={false}
                onClick={() => {
                  metrics.logEvent('AIResponseReviewView.CloseButtonClicked');
                  onDone();
                }}
              >
                <span>{t('close', 'Close')}</span>
              </NavigationButton>
            )}
          </NavigationButtonsContainer>
        )}
      </BodyContainer>
    </ModalWrapper>
  );
};

export default AIResponseReviewView;

const GreatPickTag = styled.div`
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 2;
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 30px;
    height: 30px;
    color: ${theme.colors.betaYellow};
  }

  span {
    line-height: 1;
  }
`;

const BodyContainer = styled.div`
  position: relative;
  padding: 20px;
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  height: 100%;
`;

const CenteredImageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  height: 100%;
`;

const CenteredResponseWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const StyledResponseAndFeedback = styled.div`
  position: relative;
  width: 100%;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  justify-content: center;
`;

const ResponseBlurContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  background: ${theme.colors.white};
  border-radius: 10px;
  overflow: hidden;

  :after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 40px;
    background: linear-gradient(
      to bottom,
      ${theme.colors.white}00,
      ${theme.colors.white}
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const ResponseContainer = styled.span`
  position: relative;
  font-weight: bold;
  margin-bottom: 0px;
  padding: 0px 15px;
  font-size: 40px;
  width: 100%;
  height: 100%;
  max-height: 100%;
  white-space: pre-wrap;
  position: relative;
  background: ${theme.colors.white};
  color: ${theme.colors.black};
  @media (max-width: 992px) {
    font-size: 22px;
  }
  border-radius: 10px;
  padding: 40px 0px 0px 0px;
  display: flex;
  flex-direction: column;
`;

const ResponseScrollInner = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  font-size: 28px;
  padding-left: 10px;
  padding-right: 10px;
  padding-bottom: 20px; // Scroll padding,
`;

const FeedbackContainer = styled.span`
  font-weight: bold;
  margin-bottom: 0px;
  padding: 0px 15px;
  font-size: 40px;
  width: 100%;
  white-space: pre-wrap;
  @media (max-width: 992px) {
    font-size: 22px;
  }
  @media (max-width: 992px) {
    font-size: 22px;
  }
`;

const ResponseHeader = styled.span`
  position: absolute;
  align-items: center;
  justify-content: center;
  top: 0px;
  left: 0px;
  font-size: 16px;
  height: 32px;
  line-height: 20px;
  user-select: none;
  font-weight: bold;
  margin-bottom: 10px;
  color: ${theme.colors.white};
  background: ${theme.colors.blue};
  padding: 5px 10px;
  border-radius: 10px 0px 10px 0px;
`;

const ModalWrapper = styled(Modal)`
  max-width: 100vw;
  .ant-modal-content {
    border-radius: 10px;
    box-shadow: none;
    margin: 0;
    color: ${theme.colors.white};
    width: 100vw;
  }

  .ant-modal-body {
    background: ${theme.colors.blackPure};
    box-shadow: none;
    height: 100vh;
  }
`;

const NavigationButtonsContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 10;
  background: ${theme.colors.translucentLightBlack};
  padding: 10px;
  border-radius: 10px;
`;

const NavigationButton = styled(Button)<{ $isDisabled: boolean; $isOrange?: boolean }>`
  width: 130px;
  height: 40px;
  border-radius: 5px;
  background-color: ${({ $isOrange, $isDisabled }) =>
    $isOrange && !$isDisabled ? '#ff8c42' : theme.colors.greyDark};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: ${({ $isDisabled, $isOrange }) => {
      if ($isDisabled) return theme.colors.greyDark;
      if ($isOrange) return '#ff7a29';
      return theme.colors.iconGrey;
    }};
    transform: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'scale(1.05)')};
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.95);
    background-color: ${theme.colors.iconGrey};
    color: ${theme.colors.white};
  }

  &:focus {
    background-color: ${theme.colors.iconGrey};
    color: ${theme.colors.white};
  }

  &:disabled {
    cursor: default;
  }

  svg {
    height: 30px;
    width: 30px;
  }
`;

const DoneReviewingButton = styled(Button)<{ $isActive: boolean }>`
  width: 160px;
  height: 40px;
  border-radius: 5px;
  background-color: ${({ $isActive }) => ($isActive ? '#ff8c42' : theme.colors.greyDark)};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  font-weight: 600;

  span {
    color: ${theme.colors.white};
    font-size: 14px;
  }

  &:hover {
    background-color: ${({ $isActive }) =>
      $isActive ? '#ff7a29' : theme.colors.iconGrey};
    transform: scale(1.05);
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.95);
    background-color: #ff6810;
    color: ${theme.colors.white};
  }

  &:focus {
    background-color: #ff7a29;
    color: ${theme.colors.white};
  }
`;
